import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr } from "@/lib/api";
import { Link } from "react-router-dom";
import { TrendingUp, Utensils, AlertTriangle, Trash2, X, Download } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell } from "recharts";

const today = () => new Date().toISOString().slice(0, 10);

const SLOT_COLORS = {
  breakfast: "#D97706",
  lunch:     "#059669",
  snacks:    "#7C3AED",
};

function csvDownload(rows, filename) {
  if (!rows || rows.length === 0) return;
  const headers = ["slot", "student_name", "student_id", "grade", "rfid_card_id", "plan_name", "plan_type"];
  const csv = [headers.join(",")].concat(
    rows.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))
  ).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const KPI = ({ label, value, sub, icon: Icon, accent = "bg-cream-200 text-brand", testid }) => (
  <div className="bg-white rounded-2xl p-5 border border-border" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">{label}</div>
        <div className="font-heading text-3xl font-extrabold text-ink mt-2">{value}</div>
        {sub && <div className="text-xs text-ink-500 mt-1">{sub}</div>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon size={18} strokeWidth={2.4} />
      </div>
    </div>
  </div>
);

function DrillModal({ data, onClose }) {
  if (!data) return null;
  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="drill-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div>
            <h3 className="font-heading text-xl font-extrabold capitalize" data-testid="drill-title">
              {data.kind} · {data.slot}
            </h3>
            <div className="text-xs text-ink-500 mt-0.5">{data.rows.length} student{data.rows.length !== 1 ? "s" : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="drill-csv" disabled={data.rows.length === 0}
              onClick={() => csvDownload(data.rows, `${data.kind}-${data.slot}-${today()}.csv`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ink text-white text-xs font-semibold hover:bg-ink-700 disabled:opacity-30">
              <Download size={12} /> CSV
            </button>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-cream-200"><X size={18} /></button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-sm">
            <thead className="bg-cream-200 sticky top-0">
              <tr>
                <th className="text-left p-3 text-xs uppercase font-bold">Student</th>
                <th className="text-left p-3 text-xs uppercase font-bold">Grade</th>
                <th className="text-left p-3 text-xs uppercase font-bold">RFID</th>
                <th className="text-left p-3 text-xs uppercase font-bold">Plan</th>
                <th className="text-left p-3 text-xs uppercase font-bold">Type</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-border" data-testid={`drill-row-${i}`}>
                  <td className="p-3 font-semibold">{r.student_name}<div className="text-[10px] text-ink-500">{r.student_id}</div></td>
                  <td className="p-3 text-ink-500">{r.grade || "—"}</td>
                  <td className="p-3 text-ink-500 font-mono text-xs">{r.rfid_card_id || "—"}</td>
                  <td className="p-3">{r.plan_name || "—"}</td>
                  <td className="p-3"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-cream-200">{r.plan_type}</span></td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={5} className="p-10 text-center text-ink-500">No students in this bucket.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/dashboard/wastage?date=${date}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [date]);

  const collectedChart = (data?.by_slot || []).filter(s => s.slot !== "dinner").map(s => ({
    slot: s.slot,
    Subscription: s.subscription_collected || 0,
    "Pre-order": s.preorder_collected || 0,
  }));
  const uncollectedChart = (data?.by_slot || []).filter(s => s.slot !== "dinner").map(s => ({
    slot: s.slot,
    Uncollected: s.uncollected || 0,
    fill: SLOT_COLORS[s.slot] || "#94A3B8",
  }));

  const onClickCollected = (slot) => {
    const row = (data?.by_slot || []).find(s => s.slot === slot);
    if (!row) return;
    setDrill({ kind: "Collected", slot, rows: row.collected_students || [] });
  };
  const onClickUncollected = (slot) => {
    const row = (data?.by_slot || []).find(s => s.slot === slot);
    if (!row) return;
    setDrill({ kind: "Uncollected (wastage)", slot, rows: row.uncollected_students || [] });
  };

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Admin · Live</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Today at a glance</h1>
          </div>
          <input data-testid="dash-date" type="date" value={date} onChange={e => setDate(e.target.value)}
            className="rounded-xl border border-border px-3 py-2 text-sm bg-white" />
        </div>

        {loading && <div className="text-sm text-ink-500">Loading…</div>}

        {!loading && data && (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <KPI testid="kpi-taps" label="Daily Taps"
                value={data.total_taps}
                sub={`of ${data.expected} expected`}
                icon={Utensils} accent="bg-emerald-50 text-emerald-700" />
              <KPI testid="kpi-preorders" label="Pre-orders today"
                value={data.preorder_count_today}
                sub={inr(data.preorder_value_today || 0)}
                icon={TrendingUp} accent="bg-amber-50 text-amber-700" />
              <KPI testid="kpi-uncollected" label="Uncollected (wastage)"
                value={data.uncollected}
                sub="subscriptions + pre-orders + specials"
                icon={Trash2} accent="bg-rose-50 text-rose-700" />
              <KPI testid="kpi-wastage-pct" label="Wastage %"
                value={`${data.wastage_pct}%`}
                sub={data.wastage_pct > 15 ? "Above 15% — review menu" : "Healthy"}
                icon={AlertTriangle}
                accent={data.wastage_pct > 15 ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"} />
            </div>

            {/* Two charts side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Collected by meal */}
              <div className="bg-white rounded-2xl border border-border p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Taps by meal</div>
                    <div className="font-heading text-xl font-bold mt-1">Subscription + Pre-order</div>
                  </div>
                  <div className="text-[11px] text-ink-500">Click a bar for the student list</div>
                </div>
                <div className="h-72">
                  <ResponsiveContainer>
                    <BarChart data={collectedChart} margin={{ top: 10, right: 12, left: -10, bottom: 6 }}>
                      <CartesianGrid stroke="#F3F1ED" vertical={false} />
                      <XAxis dataKey="slot" tickLine={false} axisLine={false} style={{ textTransform: "capitalize", fontSize: 11 }} />
                      <YAxis tickLine={false} axisLine={false} style={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip cursor={{ fill: "#F3F1ED" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Subscription" stackId="a" fill="#0F172A" radius={[0, 0, 0, 0]}
                        onClick={(payload) => onClickCollected(payload.slot)}
                        cursor="pointer" />
                      <Bar dataKey="Pre-order" stackId="a" fill="#D97706" radius={[6, 6, 0, 0]}
                        onClick={(payload) => onClickCollected(payload.slot)}
                        cursor="pointer" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Uncollected by meal */}
              <div className="bg-white rounded-2xl border border-border p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Uncollected by meal</div>
                    <div className="font-heading text-xl font-bold mt-1">Wastage breakdown</div>
                  </div>
                  <div className="text-[11px] text-ink-500">Click a bar to see who didn't tap</div>
                </div>
                <div className="h-72">
                  <ResponsiveContainer>
                    <BarChart data={uncollectedChart} margin={{ top: 10, right: 12, left: -10, bottom: 6 }}>
                      <CartesianGrid stroke="#F3F1ED" vertical={false} />
                      <XAxis dataKey="slot" tickLine={false} axisLine={false} style={{ textTransform: "capitalize", fontSize: 11 }} />
                      <YAxis tickLine={false} axisLine={false} style={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip cursor={{ fill: "#FEF2F2" }} />
                      <Bar dataKey="Uncollected" radius={[6, 6, 0, 0]}
                        onClick={(payload) => onClickUncollected(payload.slot)} cursor="pointer">
                        {uncollectedChart.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Per-slot table for accessibility */}
            <div className="mt-6 bg-white rounded-2xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-cream-200">
                  <tr>
                    <th className="text-left p-3 text-xs uppercase font-bold">Slot</th>
                    <th className="text-right p-3 text-xs uppercase font-bold">Expected</th>
                    <th className="text-right p-3 text-xs uppercase font-bold">Collected</th>
                    <th className="text-right p-3 text-xs uppercase font-bold">Uncollected</th>
                    <th className="text-right p-3 text-xs uppercase font-bold">Wastage %</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_slot || []).filter(s => s.slot !== "dinner").map(s => (
                    <tr key={s.slot} className="border-t border-border"
                      data-testid={`slot-row-${s.slot}`}>
                      <td className="p-3 font-semibold capitalize">{s.slot}</td>
                      <td className="p-3 text-right">{s.expected}</td>
                      <td className="p-3 text-right">
                        <button data-testid={`drill-collected-${s.slot}`} onClick={() => onClickCollected(s.slot)}
                          className="text-emerald-700 font-bold hover:underline">{s.collected}</button>
                      </td>
                      <td className="p-3 text-right">
                        <button data-testid={`drill-uncollected-${s.slot}`} onClick={() => onClickUncollected(s.slot)}
                          className="text-rose-700 font-bold hover:underline">{s.uncollected}</button>
                      </td>
                      <td className="p-3 text-right">
                        {s.expected > 0 ? `${Math.round((s.uncollected / s.expected) * 1000) / 10}%` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <DrillModal data={drill} onClose={() => setDrill(null)} />
    </AppShell>
  );
}
